import { AnalyticsEventName } from '@/analytics/analytics.events';
import { track } from '@/analytics/track';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type InfiniteData } from '@tanstack/react-query';
import { apiJson } from '@/api/apiFetch';
import { useAuth } from '@/contexts/AuthContext';
import { doNothing } from '@/utils/doNothing';
import { disableTranslations } from './TranslationScheduler';
import { PENDING_CONFIRMATIONS_QUERY_KEY } from './MustReads.keys';
import {
  createKhovaNewsfeedPost,
  deleteKhovaNewsfeedPost,
  khovaNewsfeedTransport as transport,
  updateKhovaNewsfeedPost,
} from './Newsfeed.transport';
import type {
  CommentsResponse,
  CreatePostPayload,
  FeedPage,
  NewsfeedComment,
  NewsfeedItem,
  NewsfeedTranslation,
  TranslatePostResponse,
  TranslatePostVariables,
  UpdatePostPayload,
} from './Newsfeed.types';

const PAGE_SIZE = 20;

interface UseNewsfeedQueryArgs {
  limit?: number;
  offset?: number;
  unreadOnly?: boolean;
}

export const useNewsfeedQuery = ({ limit = 2, offset = 0, unreadOnly = true }: UseNewsfeedQueryArgs = {}) => {
  const { user } = useAuth();
  const locale = user?.languageSlug ?? null;
  return useQuery({
    queryKey: ['newsfeed', { limit, offset, unreadOnly, locale }],
    queryFn: () => transport.fetchFeed({ limit, offset, unreadOnly, locale }),
    retry: 1,
  });
};

export const useNewsfeedUnreadCountQuery = (options: { enabled?: boolean } = {}) => {
  const { enabled = true } = options;
  return useQuery({
    queryKey: ['newsfeed-unread-count'],
    queryFn: async () => {
      const res = await transport.fetchFeed({ limit: 1, offset: 0, unreadOnly: true, countOnly: true });
      return res.pagination.rowCount;
    },
    enabled,
  });
};

interface UseInfiniteNewsfeedArgs {
  unreadOnly?: boolean;
}

export const useInfiniteNewsfeedQuery = ({ unreadOnly = false }: UseInfiniteNewsfeedArgs = {}) => {
  const { user } = useAuth();
  const locale = user?.languageSlug ?? null;
  return useInfiniteQuery({
    queryKey: ['newsfeed', 'infinite', { unreadOnly, locale }],
    initialPageParam: 0,
    queryFn: ({ pageParam }) => transport.fetchFeed({ limit: PAGE_SIZE, offset: pageParam, unreadOnly, locale }),
    getNextPageParam: (lastPage) => {
      const nextOffset = lastPage.pagination.offset + lastPage.pagination.limit;
      return nextOffset < lastPage.pagination.rowCount ? nextOffset : undefined;
    },
  });
};

interface ToggleLikeVariables {
  item: NewsfeedItem;
  like: boolean;
  source: 'list' | 'post' | 'home';
}

const applyLikeToItem = (item: NewsfeedItem, like: boolean): NewsfeedItem => {
  if (item.like === like) {
    return { ...item, unread: false };
  }
  const delta = like ? 1 : -1;
  return { ...item, like, likes: Math.max(0, item.likes + delta), unread: false };
};

const applyLikeToPage = (page: FeedPage, postId: string, like: boolean): FeedPage => ({
  ...page,
  data: page.data.map((item) => (item.id === postId ? applyLikeToItem(item, like) : item)),
});

export const useToggleLikeMutation = () => {
  const queryClient = useQueryClient();
  const markAsRead = useMarkAsReadMutation();

  return useMutation({
    mutationFn: ({ item, like }: ToggleLikeVariables) => transport.setLike(item, like),
    onMutate: async ({ item, like }) => {
      await queryClient.cancelQueries({ queryKey: ['newsfeed'] });
      await queryClient.cancelQueries({ queryKey: ['newsfeed-item', item.id] });
      const listSnapshots = queryClient.getQueriesData<FeedPage | InfiniteData<FeedPage>>({ queryKey: ['newsfeed'] });
      const itemSnapshots = queryClient.getQueriesData<NewsfeedItem>({ queryKey: ['newsfeed-item', item.id] });
      queryClient.setQueriesData<FeedPage | InfiniteData<FeedPage>>({ queryKey: ['newsfeed'] }, (existing) => {
        if (!existing) {
          return existing;
        }
        if ('pages' in existing) {
          return {
            ...existing,
            pages: existing.pages.map((page) => applyLikeToPage(page, item.id, like)),
          };
        }
        return applyLikeToPage(existing, item.id, like);
      });
      queryClient.setQueriesData<NewsfeedItem>({ queryKey: ['newsfeed-item', item.id] }, (existing) =>
        existing ? applyLikeToItem(existing, like) : existing,
      );
      return { listSnapshots, itemSnapshots };
    },
    onSuccess: (_data, { item, like, source }) => {
      track(like ? AnalyticsEventName.NEWSFEED_POST_LIKED : AnalyticsEventName.NEWSFEED_POST_UNLIKED, {
        post_id: item.id,
        source,
      });
      queryClient.invalidateQueries({ queryKey: ['newsfeed-unread-count'] }).catch(doNothing);
      if (item.unread) {
        markAsRead.mutate(item.id);
      }
    },
    onError: (_err, _vars, context) => {
      context?.listSnapshots.forEach(([key, data]) => queryClient.setQueryData(key, data));
      context?.itemSnapshots.forEach(([key, data]) => queryClient.setQueryData(key, data));
    },
  });
};

const applyTranslationToItem = (item: NewsfeedItem, res: TranslatePostResponse): NewsfeedItem => {
  if (!res.translated) {
    return { ...item, translation: null };
  }
  const translation: NewsfeedTranslation = {
    title: res.title ?? '',
    subtitle: res.subtitle ?? '',
    description: res.description ?? '',
    answers: (res.answers ?? []).map((answer) => ({ id: String(answer.id), text: answer.text })),
  };
  return { ...item, translation };
};

const queryKeyHasLocale = (queryKey: readonly unknown[], locale: string): boolean =>
  queryKey.some(
    (part) =>
      typeof part === 'object' &&
      part !== null &&
      'locale' in part &&
      (part as { locale?: string | null }).locale === locale,
  );

const applyTranslationToPage = (page: FeedPage, postId: string, res: TranslatePostResponse): FeedPage => ({
  ...page,
  data: page.data.map((item) => (item.id === postId ? applyTranslationToItem(item, res) : item)),
});

export const useTranslatePostMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ postId, locale }: TranslatePostVariables) => {
      const res = await transport.translatePost(postId, locale);
      if (!res) {
        disableTranslations();
      }
      return res;
    },
    onSuccess: (res, { postId, locale }) => {
      if (!res) {
        return;
      }
      queryClient.setQueriesData<FeedPage | InfiniteData<FeedPage>>(
        { queryKey: ['newsfeed'], predicate: (query) => queryKeyHasLocale(query.queryKey, locale) },
        (existing) => {
          if (!existing) {
            return existing;
          }
          if ('pages' in existing) {
            return {
              ...existing,
              pages: existing.pages.map((page) => applyTranslationToPage(page, postId, res)),
            };
          }
          return applyTranslationToPage(existing, postId, res);
        },
      );
      queryClient.setQueriesData<NewsfeedItem>(
        { queryKey: ['newsfeed-item', postId], predicate: (query) => query.queryKey[2] === locale },
        (existing) => (existing ? applyTranslationToItem(existing, res) : existing),
      );
      queryClient.setQueriesData<NewsfeedItem[]>(
        { queryKey: PENDING_CONFIRMATIONS_QUERY_KEY, predicate: (query) => query.queryKey[1] === locale },
        (existing) => existing?.map((item) => (item.id === postId ? applyTranslationToItem(item, res) : item)),
      );
    },
  });
};

export const useCommentsQuery = (postId: string, enabled: boolean) => {
  return useQuery({
    queryKey: ['newsfeed-comments', postId],
    queryFn: () => transport.fetchComments(postId),
    enabled,
  });
};

const bumpCommentsCount = (page: FeedPage, postId: string, delta: number): FeedPage => ({
  ...page,
  data: page.data.map((item) =>
    item.id === postId
      ? {
          ...item,
          metrics: { ...item.metrics, comments: Math.max(0, item.metrics.comments + delta) },
          unread: delta > 0 ? false : item.unread,
        }
      : item,
  ),
});

export const useNewsfeedItemQuery = (postId: string | undefined) => {
  const { user } = useAuth();
  const locale = user?.languageSlug ?? null;
  return useQuery({
    queryKey: ['newsfeed-item', postId, locale],
    queryFn: () => transport.fetchPost(postId, locale),
    enabled: Boolean(postId),
  });
};

const inFlightReads = new Map<string, Promise<void>>();

export const useMarkAsReadMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (postId: string) => {
      const inFlight = inFlightReads.get(postId);
      if (inFlight) {
        return inFlight;
      }
      const read = transport.markRead(postId).finally(() => inFlightReads.delete(postId));
      inFlightReads.set(postId, read);
      return read;
    },
    onSuccess: (_data, postId) => {
      queryClient.setQueriesData<FeedPage | InfiniteData<FeedPage>>({ queryKey: ['newsfeed'] }, (existing) => {
        if (!existing) return existing;
        const markPage = (page: FeedPage): FeedPage => ({
          ...page,
          data: page.data.map((item) => (item.id === postId ? { ...item, unread: false } : item)),
        });
        if ('pages' in existing) {
          return { ...existing, pages: existing.pages.map(markPage) };
        }
        return markPage(existing);
      });

      queryClient.setQueriesData<NewsfeedItem>({ queryKey: ['newsfeed-item', postId] }, (existing) =>
        existing ? { ...existing, unread: false } : existing,
      );

      queryClient.invalidateQueries({ queryKey: ['newsfeed'], refetchType: 'none' }).catch(doNothing);
      queryClient.invalidateQueries({ queryKey: ['newsfeed-unread-count'] }).catch(doNothing);
    },
  });
};

interface SubmitPollAnswerArgs {
  postId: string;
  answerId: string;
  unread: boolean;
}

const applyPollAnswerToItem = (item: NewsfeedItem, answerId: string): NewsfeedItem => ({
  ...item,
  unread: false,
  answers: item.answers.map((answer) =>
    answer.id === answerId
      ? { ...answer, answered: true, answersCount: answer.answersCount + 1 }
      : { ...answer, answered: false },
  ),
});

export const useSubmitPollAnswerMutation = () => {
  const queryClient = useQueryClient();
  const markAsRead = useMarkAsReadMutation();
  return useMutation({
    mutationFn: ({ postId, answerId }: SubmitPollAnswerArgs) => transport.answerPoll(postId, answerId),
    onSuccess: (_data, { postId, answerId, unread }) => {
      track(AnalyticsEventName.NEWSFEED_POLL_ANSWERED, { post_id: postId, answer_id: answerId });
      queryClient.invalidateQueries({ queryKey: ['newsfeed-unread-count'] }).catch(doNothing);
      if (unread) {
        markAsRead.mutate(postId);
      }
      queryClient.setQueriesData<NewsfeedItem>({ queryKey: ['newsfeed-item', postId] }, (existing) =>
        existing ? applyPollAnswerToItem(existing, answerId) : existing,
      );
      queryClient.setQueriesData<FeedPage | InfiniteData<FeedPage>>({ queryKey: ['newsfeed'] }, (existing) => {
        if (!existing) {
          return existing;
        }
        const updatePage = (page: FeedPage): FeedPage => ({
          ...page,
          data: page.data.map((item) => (item.id === postId ? applyPollAnswerToItem(item, answerId) : item)),
        });
        if ('pages' in existing) {
          return { ...existing, pages: existing.pages.map(updatePage) };
        }
        return updatePage(existing);
      });
    },
  });
};

const bumpItemCommentsCount = (item: NewsfeedItem, delta: number): NewsfeedItem => ({
  ...item,
  metrics: { ...item.metrics, comments: Math.max(0, item.metrics.comments + delta) },
  unread: delta > 0 ? false : item.unread,
});

export const useAddCommentMutation = (postId: string, source: 'list' | 'post' | 'home') => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (content: string) => transport.addComment(postId, content),
    onSuccess: (newComment) => {
      track(AnalyticsEventName.NEWSFEED_POST_COMMENTED, { post_id: postId, source });
      queryClient.invalidateQueries({ queryKey: ['newsfeed-unread-count'] }).catch(doNothing);
      queryClient.setQueryData<CommentsResponse>(['newsfeed-comments', postId], (existing) => {
        if (!existing) {
          return {
            data: [newComment],
            pagination: { rowCount: 1, limit: 50, offset: 0 },
          };
        }
        return {
          ...existing,
          data: [newComment, ...existing.data],
          pagination: {
            ...existing.pagination,
            rowCount: existing.pagination.rowCount + 1,
          },
        };
      });
      queryClient.invalidateQueries({ queryKey: ['newsfeed-comments', postId] }).catch(doNothing);

      queryClient.setQueriesData<FeedPage | InfiniteData<FeedPage>>({ queryKey: ['newsfeed'] }, (existing) => {
        if (!existing) {
          return existing;
        }
        if ('pages' in existing) {
          return { ...existing, pages: existing.pages.map((page) => bumpCommentsCount(page, postId, 1)) };
        }
        return bumpCommentsCount(existing, postId, 1);
      });
      queryClient.setQueriesData<NewsfeedItem>({ queryKey: ['newsfeed-item', postId] }, (existing) =>
        existing ? bumpItemCommentsCount(existing, 1) : existing,
      );
    },
  });
};

/** Admins and managers publish to the Khova newsfeed (contract §9); the feed refetches after. */
export const useCreatePostMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreatePostPayload) => createKhovaNewsfeedPost(payload),
    onSuccess: (post, { requiresConfirmation }) => {
      if (requiresConfirmation) {
        track(AnalyticsEventName.NEWSFEED_MUST_READ_CREATED, { post_id: post.id });
      }
      queryClient.invalidateQueries({ queryKey: ['newsfeed'] }).catch(doNothing);
    },
  });
};

interface UpdatePostVariables {
  postId: string;
  payload: UpdatePostPayload;
}

/**
 * The author (or an admin) edits a published post. The refetch of the feed and the post is awaited,
 * so the editor closes (and can be reopened) only once the edited post is in the cache. A failed
 * refetch fails the save: the editor stays open instead of closing on the old text, which a later
 * save would write back. Saving again sends the same content, so retrying is safe.
 */
export const useUpdatePostMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ postId, payload }: UpdatePostVariables) => updateKhovaNewsfeedPost(postId, payload),
    onSuccess: (_data, { postId }) =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ['newsfeed'] }, { throwOnError: true }),
        queryClient.invalidateQueries({ queryKey: ['newsfeed-item', postId] }, { throwOnError: true }),
      ]),
  });
};

/**
 * The author (or an admin) deletes a post; it leaves the feed and the unread count. The post's own
 * query is left to expire: removing it while its detail page is still mounted would refetch a 404.
 */
export const useDeletePostMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (postId: string) => deleteKhovaNewsfeedPost(postId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['newsfeed'] }).catch(doNothing);
      queryClient.invalidateQueries({ queryKey: ['newsfeed-unread-count'] }).catch(doNothing);
    },
  });
};

export interface AudienceStore {
  id: string;
  name: string;
}

export interface AudienceUser {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
}

/** Stores a new post can target: all of the account for admins, their own for managers. */
export const listAudienceStores = (): Promise<AudienceStore[]> => apiJson<AudienceStore[]>('/newsfeed/audience/stores');

/** Active users of the account an admin can target directly. */
export const listAudienceUsers = (search: string): Promise<AudienceUser[]> =>
  apiJson<AudienceUser[]>(`/newsfeed/audience/users?search=${encodeURIComponent(search.trim())}`);
